/**
 * Facing direction + its provenance, as one indivisible value.
 *
 * The Phase 1 contract pins `facing_source` JOINTLY with `facing_direction`:
 * the source is null if and only if the direction is null. Two independent
 * `useState` calls cannot express that — one writer forgetting its sibling
 * silently breaks the pin, and there are SEVEN writers in CreateTemplate.
 *
 * So every transition is a named function here, each returning the complete
 * pair, and `CreateTemplate.tsx` calls one of them at each writer. The pin
 * then holds by construction, and this module (not a re-implementation of the
 * component's logic) is what the tests exercise.
 *
 * Why the provenance matters at all: downstream, a coach's own declaration
 * outranks a detector guess, and "the detector guessed and nobody looked" has
 * to stay distinguishable from "the coach confirmed the detector was right".
 * The pre-existing `facing_confidence` number cannot express that difference —
 * a high-confidence guess and a coach decision are not the same fact.
 */

export type FacingDirection = 'left' | 'right';

export type FacingSource = 'detected' | 'detected_unconfirmed' | 'coach' | null;

export interface FacingState {
  direction: FacingDirection | null;
  source: FacingSource;
}

/** The empty pair — nothing declared. */
export const FACING_CLEARED: FacingState = { direction: null, source: null };

/**
 * WRITER 1 — loading a saved template.
 *
 * A template saved before `facing_source` existed has a direction but no
 * source. It is read as `detected_unconfirmed`: the value came from the
 * detector and nobody has confirmed it. Claiming `coach` would invent a human
 * decision that never happened; claiming null would break the joint pin.
 */
export function facingFromStored(
  direction: FacingDirection | null | undefined,
  storedSource: FacingSource | undefined,
): FacingState {
  if (!direction) return FACING_CLEARED;
  return { direction, source: storedSource ?? 'detected_unconfirmed' };
}

/**
 * WRITER 2 — the detector was confident enough to apply its answer without
 * asking. Nobody has confirmed it, but nobody dismissed a dialog either.
 */
export function facingFromDetector(direction: FacingDirection): FacingState {
  return { direction, source: 'detected' };
}

/**
 * WRITERS 3, 4 and 6 — the coach confirmed the detector, corrected it, or
 * picked a direction in the selector. All three are the same fact: a human
 * said so.
 */
export function facingFromCoach(direction: FacingDirection): FacingState {
  return { direction, source: 'coach' };
}

/**
 * WRITER 5 — the coach closed the confirmation dialog without choosing. The
 * dialog still keeps the detected VALUE, so the source must record that it is
 * unconfirmed.
 */
export function facingFromDismissedDialog(direction: FacingDirection): FacingState {
  return { direction, source: 'detected_unconfirmed' };
}

/**
 * WRITER 7 — the coach cleared the field (the Clear button, or toggling the
 * selected direction chip off).
 */
export function facingCleared(): FacingState {
  return FACING_CLEARED;
}

/**
 * The selector's click handler: clicking the SELECTED chip clears the pair,
 * clicking any other chip is a coach declaration.
 *
 * `clearable` is false wherever the backend requires the field (every camera
 * view except diagonal), so the coach cannot one-click their way into a
 * template that will be refused at finalize. When it is false, clicking the
 * already-selected chip is a no-op and the current state is returned
 * unchanged.
 */
export function facingSelectorClick(
  current: FacingState,
  clicked: FacingDirection,
  clearable: boolean,
): FacingState {
  if (current.direction === clicked) {
    return clearable ? facingCleared() : current;
  }
  return facingFromCoach(clicked);
}

/**
 * The joint pin, as a predicate: source is null if and only if direction is
 * null. Exported so the contract can be asserted directly rather than
 * inferred from each writer's output.
 */
export function satisfiesFacingJointPin(state: FacingState): boolean {
  return (state.direction === null) === (state.source === null);
}
